import { useMutation, useQuery } from "@tanstack/react-query";
import {
  deleteReferenceById,
  searchReferenceData,
  importReferenceDataCsv,
  createReferenceData,
  updateReferenceData,
} from "../api/ReferenceApi";
import { toast } from "react-toastify";
import { handleApiError } from "../api/InstanceApi";
import { useIntl } from "react-intl";

export const useReferenceData = (searchParams) => {
  const intl = useIntl();
  const {
    data: referenceData,
    isLoading: referenceDataLoading,
    error: referenceDataError,
    isFetching: isReferenceDataFetching,
  } = useQuery({
    queryKey: [
      "reference-data",
      searchParams?.page,
      searchParams?.limit,
      searchParams?.name,
      searchParams?.data,
    ],
    queryFn: () => searchReferenceData(searchParams),
    staleTime: 0,
  });
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      return await toast.promise(deleteReferenceById(id), {
        pending: {
          render() {
            return `${intl.formatMessage({ id: "AGENTS.REFERENCE_DATA.DELETING" })}`;
          },
        },
        success: {
          render() {
            return `${intl.formatMessage({ id: "AGENTS.REFERENCE_DATA.DELETED" })}`;
          },
        },
        error: {
          render({ data }) {
            return data.message;
          },
        },
      });
    },
    onError: (data) => handleApiError(data),
  });
  const importMutation = useMutation({
    mutationFn: async (file) => {
      return await toast.promise(importReferenceDataCsv(file), {
        pending: {
          render() {
            return `${intl.formatMessage({ id: "AGENTS.REFERENCE_DATA.IMPORTING" })}`;
          },
        },
        success: {
          render() {
            return `${intl.formatMessage({ id: "AGENTS.REFERENCE_DATA.IMPORTED" })}`;
          },
        },
        error: {
          render({ data }) {
            return data.message;
          },
        },
      });
    },
    onError: (data) => handleApiError(data),
  });
  const createMutation = useMutation({
    mutationFn: async (data) => {
      return await toast.promise(createReferenceData(data), {
        pending: {
          render() {
            return `${intl.formatMessage({ id: "AGENTS.REFERENCE_DATA.CREATING" })}`;
          },
        },
        success: {
          render() {
            return `${intl.formatMessage({ id: "AGENTS.REFERENCE_DATA.CREATED" })}`;
          },
        },
        error: {
          render({ data }) {
            return data.message;
          },
        },
      });
    },
    onError: (data) => handleApiError(data),
  });

  const updateMutation = useMutation({
    mutationFn: async (data) => {
      return await toast.promise(updateReferenceData(data), {
        pending: {
          render() {
            return `${intl.formatMessage({ id: "AGENTS.REFERENCE_DATA.UPDATING" })}`;
          },
        },
        success: {
          render() {
            return `${intl.formatMessage({ id: "AGENTS.REFERENCE_DATA.UPDATED" })}`;
          },
        },
        error: {
          render({ data }) {
            return data.message;
          },
        },
      });
    },
    onError: (data) => handleApiError(data),
  });
  return {
    referenceData,
    referenceDataLoading,
    referenceDataError,
    isReferenceDataFetching,
    deleteReferenceByIdAsync: deleteMutation.mutateAsync,
    importReferenceDataCsvAsync: importMutation.mutateAsync,
    createReferenceDataAsync: createMutation.mutateAsync,
    updateReferenceDataAsync: updateMutation.mutateAsync,
  };
};
